import { useContext, useEffect, useState, useRef } from 'react'
import { ConferenceContext } from 'contexts/conference'
import { ConferenceParticipant } from 'contexts/conference/types'

type UseRenderVideoResult = {
    isVideoRendered: boolean
}

const VIDEO_RENDER_DELAY_MS = 600

const useRenderVideo = (
    participant: ConferenceParticipant,
    div: HTMLDivElement | null,
    stopVideoRender: boolean
): UseRenderVideoResult => {
    const conference = useContext(ConferenceContext)
    const [isVideoRendered, setIsVideoRendered] = useState(false)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const clearRenderTimeout = (): void => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }

    useEffect(() => {
        const updateVideoFrame = async (): Promise<void> => {
            const shouldRenderVideo =
                conference.status === 'session-joined' &&
                div !== null &&
                !stopVideoRender &&
                participant.cameraStatus === 'on'

            if (shouldRenderVideo) {
                const container = document.getElementById('video-player-container')?.getBoundingClientRect()
                const containerLeft = container?.left ?? 0
                const containerTop = container?.top ?? 0
                const bounds = div.getBoundingClientRect()

                await conference.rtcSession.setVideoFrame(participant, {
                    left: bounds.x + 2 - containerLeft,
                    top: bounds.y + 3 - containerTop,
                    height: bounds.height - 6,
                    width: bounds.width - 6
                })

                clearRenderTimeout()
                timeoutRef.current = setTimeout(() => {
                    setIsVideoRendered(true)
                }, VIDEO_RENDER_DELAY_MS)
            } else if (participant.cameraStatus === 'off' || stopVideoRender) {
                setIsVideoRendered(false)
                clearRenderTimeout()
            }
        }

        void updateVideoFrame()
    }, [conference.participants, conference.speakers, conference.videoPlayerSize])

    useEffect(() => {
        return clearRenderTimeout
    }, [])

    return { isVideoRendered }
}

export { useRenderVideo }
